import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import {
  Alert,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  Tabs,
} from '@/components/ui'
import { fetchWhatsappInbox, fetchWhatsappStatus, fetchWhatsappTemplates } from './api'
import type { WhatsappContato } from './types'

type TabId = 'inbox' | 'templates'

export function WhatsappPage() {
  const [tab, setTab] = useState<TabId>('inbox')
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState('todos')
  const [selId, setSelId] = useState<string | null>(null)

  const status = useQuery({
    queryKey: ['whatsapp', 'status'],
    queryFn: () => fetchWhatsappStatus(),
  })
  const inbox = useQuery({
    queryKey: ['whatsapp', 'inbox'],
    queryFn: () => fetchWhatsappInbox(),
  })
  const templates = useQuery({
    queryKey: ['whatsapp', 'templates'],
    queryFn: () => fetchWhatsappTemplates(),
    enabled: tab === 'templates',
  })

  const conversas: WhatsappContato[] = inbox.data?.dados || []
  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase()
    return conversas.filter((c) => {
      if (filtro === 'candidato' && c.tipo !== 'candidato') return false
      if (filtro === 'colaborador' && c.tipo !== 'colaborador') return false
      if (!q) return true
      const blob = `${c.nome || ''} ${c.telefone || ''} ${c.vaga || ''} ${c.depto || ''}`.toLowerCase()
      return blob.includes(q)
    })
  }, [conversas, busca, filtro])

  const selecionado = filtradas.find((c) => c.id === selId) || filtradas[0] || null
  const naoLidas = inbox.data?.nao_lidas ?? conversas.reduce((s, c) => s + (c.naoLidas || 0), 0)

  return (
    <section className="hi-page">
      <PageHeader
        title="WhatsApp inbox"
        subtitle="Inbox Business para candidatos e colaboradores. Integração de provedor pendente."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/whatsapp-inbox')}>
            Legado
          </a>
        }
      />

      {status.data && !status.data.configurado ? (
        <Alert tone="warning">{status.data.mensagem || 'Provedor WhatsApp não configurado.'}</Alert>
      ) : null}
      {status.isError ? (
        <Alert tone="danger">
          {status.error instanceof ApiError ? status.error.message : 'Falha ao consultar status.'}
        </Alert>
      ) : null}

      <KpiGrid>
        <Kpi label="Não lidas" value={String(naoLidas)} />
        <Kpi
          label="Candidatos"
          value={String(conversas.filter((c) => c.tipo === 'candidato').length)}
        />
        <Kpi
          label="Colaboradores"
          value={String(conversas.filter((c) => c.tipo === 'colaborador').length)}
        />
        <Kpi label="Conversas" value={String(conversas.length)} />
      </KpiGrid>

      <Tabs
        label="Seções WhatsApp"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'inbox', label: 'Inbox' },
          { id: 'templates', label: 'Templates' },
        ]}
      />

      {tab === 'inbox' ? (
        <>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <input
              type="search"
              placeholder="Buscar contato…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar conversas"
            />
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              aria-label="Filtrar por tipo"
            >
              <option value="todos">Todos</option>
              <option value="candidato">Candidatos</option>
              <option value="colaborador">Colaboradores</option>
            </select>
          </form>

          {inbox.isLoading ? <Muted>Carregando inbox…</Muted> : null}
          {inbox.isError ? (
            <Alert tone="danger">
              {inbox.error instanceof ApiError ? inbox.error.message : 'Falha ao carregar inbox.'}
            </Alert>
          ) : null}

          {!inbox.isLoading && filtradas.length === 0 ? (
            <Empty>
              Nenhuma conversa. Após provisionar Twilio/Z-API, as mensagens aparecerão aqui.
            </Empty>
          ) : null}

          {filtradas.length > 0 ? (
            <div className="hi-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 2fr', gap: 12 }}>
              <Section title="Conversas" flush>
                <ul className="hi-list">
                  {filtradas.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={`card card-interactive hi-row${selecionado?.id === c.id ? ' is-active' : ''}`}
                        style={{ width: '100%', textAlign: 'left' }}
                        onClick={() => setSelId(c.id)}
                      >
                        <div className="hi-row-main">
                          <strong>
                            {c.nome}
                            {(c.naoLidas || 0) > 0 ? ` (${c.naoLidas})` : ''}
                          </strong>
                          <Muted>
                            {c.ultimaMensagem || c.telefone || '—'}
                            {c.ultimaHora ? ` · ${c.ultimaHora}` : ''}
                          </Muted>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </Section>

              <Section
                title={selecionado ? selecionado.nome || 'Conversa' : 'Mensagens'}
                flush
              >
                {selecionado ? (
                  <>
                    <Muted>
                      {selecionado.telefone || '—'}
                      {selecionado.vaga ? ` · ${selecionado.vaga}` : ''}
                      {selecionado.depto ? ` · ${selecionado.depto}` : ''}
                      {selecionado.tipo ? ` · ${selecionado.tipo}` : ''}
                    </Muted>
                    <ul className="hi-list" style={{ marginTop: 12 }}>
                      {(selecionado.msgs || []).map((m, i) => (
                        <li
                          key={`${selecionado.id}-m-${i}`}
                          className="card"
                          style={{
                            marginLeft: m.de === 'empresa' ? 24 : 0,
                            marginRight: m.de === 'contato' ? 24 : 0,
                          }}
                        >
                          <Muted>{m.de === 'empresa' ? 'Você' : 'Contato'} · {m.hora}</Muted>
                          <p style={{ margin: '4px 0 0' }}>{m.texto}</p>
                        </li>
                      ))}
                    </ul>
                    {(selecionado.msgs || []).length === 0 ? (
                      <Empty>Sem mensagens nesta conversa.</Empty>
                    ) : null}
                    <Alert tone="info">
                      Envio desabilitado até a integração WhatsApp estar configurada.
                    </Alert>
                  </>
                ) : (
                  <Empty>Selecione uma conversa.</Empty>
                )}
              </Section>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'templates' ? (
        <Section title="Templates de mensagem" flush>
          {templates.isLoading ? <Muted>Carregando templates…</Muted> : null}
          {templates.isError ? (
            <Alert tone="danger">
              {templates.error instanceof ApiError
                ? templates.error.message
                : 'Falha ao carregar templates.'}
            </Alert>
          ) : null}
          {!templates.isLoading && (templates.data || []).length === 0 ? (
            <Empty>Nenhum template disponível.</Empty>
          ) : null}
          <ul className="hi-list">
            {(templates.data || []).map((t) => (
              <li key={t.id} className="card hi-row">
                <div className="hi-row-main">
                  <strong>
                    {t.icone ? `${t.icone} ` : ''}
                    {t.nome}
                  </strong>
                  <p className="hi-muted" style={{ whiteSpace: 'pre-wrap' }}>
                    {t.texto}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </section>
  )
}
